import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PhoneCall, Plus, ShieldAlert, Pause, RotateCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { nhOverview, nhNumbers, nhNumberAdd, nhNumberPause, nhNumberRestore, nhNumberGet, nhPolicyGet, nhPolicyPatch, nhEventRecord } from "@/lib/api";

const HEALTH_COLOR = {
  healthy:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  degraded: "bg-amber-50 text-amber-800 border-amber-200",
  paused:   "bg-rose-50 text-rose-700 border-rose-200",
  retired:  "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminNumberHealth() {
  const [overview, setOverview] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    try {
      const [o, n, p] = await Promise.all([nhOverview(), nhNumbers(), nhPolicyGet()]);
      setOverview(o); setNumbers(n.items || []); setPolicy(p);
    } catch (e) { setError(e?.response?.data?.detail || "Admin sign-in required."); }
  };
  useEffect(() => { document.title = "Number Health | Admin"; refresh(); }, []);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    nhNumberGet(selected).then(setDetail).catch(() => setDetail(null));
  }, [selected]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="admin-nh">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/admin" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Admin</Link>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Telephone Number Health</h1>
              <p className="mt-2 text-slate-600 max-w-2xl">Monitor spam flags, carrier errors and reputation across every number. Auto-pause thresholds are set by policy — restoration requires admin approval.</p>
            </div>
            <button onClick={() => setShowAdd(true)} data-testid="admin-nh-add-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 px-5 py-2.5 text-white font-semibold text-sm transition">
              <Plus className="w-4 h-4" /> Register number
            </button>
          </div>

          {error && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{error}</div>}

          {/* Overview stats */}
          {overview && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              <Tile label="Total" value={overview.total} tone="indigo" testId="admin-nh-total" />
              <Tile label="Healthy" value={overview.by_health.healthy || 0} tone="emerald" testId="admin-nh-healthy" />
              <Tile label="Degraded" value={overview.by_health.degraded || 0} tone="amber" testId="admin-nh-degraded" />
              <Tile label="Paused" value={overview.by_health.paused || 0} tone="rose" testId="admin-nh-paused" />
              <Tile label="Retired" value={overview.by_health.retired || 0} tone="slate" testId="admin-nh-retired" />
            </div>
          )}

          {/* Policy quick config */}
          {policy && <PolicyPanel policy={policy} onSave={async (p) => {
            const upd = await nhPolicyPatch(p); setPolicy(upd);
          }} />}

          {/* Add modal */}
          {showAdd && <AddNumberModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); refresh(); }} />}

          {/* List + detail */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <section className="md:col-span-1 rounded-2xl bg-white border border-slate-200 p-3" data-testid="admin-nh-list">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold p-2">Numbers</div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {numbers.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No numbers registered.</div>}
                {numbers.map((n) => (
                  <button key={n.id} onClick={() => setSelected(n.id)} data-testid={`admin-nh-item-${n.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition ${selected === n.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold text-slate-900 font-mono">{n.e164}</div>
                      <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 border ${HEALTH_COLOR[n.health] || HEALTH_COLOR.retired}`}>{n.health}</span>
                    </div>
                    {n.label && <div className="mt-0.5 text-xs text-slate-600">{n.label}</div>}
                    <div className="mt-1 text-[10px] text-slate-500 uppercase tracking-widest">
                      {n.country || "?"}{n.region ? ` · ${n.region}` : ""} · {n.provider || "twilio"}
                    </div>
                    {n.paused_reason && <div className="mt-1 text-[11px] text-rose-700 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5" />{n.paused_reason}</div>}
                  </button>
                ))}
              </div>
            </section>

            <section className="md:col-span-2 rounded-2xl bg-white border border-slate-200 p-5" data-testid="admin-nh-detail">
              {!detail ? (
                <div className="text-center text-sm text-slate-500 py-16">Select a number to see stats and event history.</div>
              ) : (
                <NumberDetail data={detail} onAction={async (fn) => { await fn(); const d = await nhNumberGet(selected); setDetail(d); refresh(); }} />
              )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function NumberDetail({ data, onAction }) {
  const n = data.number;
  const [pauseReason, setPauseReason] = useState("");
  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{n.provider || "twilio"} · {n.country || "?"}</div>
          <div className="text-2xl font-black text-slate-900 font-mono">{n.e164}</div>
          {n.label && <div className="text-sm text-slate-600">{n.label}</div>}
        </div>
        <span className={`text-xs uppercase tracking-widest font-bold rounded px-2 py-1 border ${HEALTH_COLOR[n.health] || HEALTH_COLOR.retired}`}>{n.health}</span>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Metric label="Calls out" value={n.stats?.calls_out || 0} />
        <Metric label="Calls in" value={n.stats?.calls_in || 0} />
        <Metric label="Answered" value={n.stats?.answered || 0} />
        <Metric label="Spam flags" value={n.stats?.spam_flags || 0} tone={n.stats?.spam_flags ? "rose" : "slate"} />
        <Metric label="Carrier errors" value={n.stats?.carrier_errors || 0} tone={n.stats?.carrier_errors ? "amber" : "slate"} />
        <Metric label="Complaints" value={n.stats?.complaints || 0} />
        <Metric label="Caller-ID reg." value={n.caller_id_registered ? "Yes" : "No"} />
        <Metric label="BI verified" value={n.business_identity_verified ? "Yes" : "No"} />
      </div>

      <div className="mt-6 flex flex-wrap gap-2 items-center">
        {n.health !== "paused" ? (
          <>
            <input value={pauseReason} onChange={(e) => setPauseReason(e.target.value)}
              data-testid="admin-nh-pause-reason"
              placeholder="Reason to pause (required)"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm flex-1 min-w-[220px]" />
            <button onClick={() => pauseReason && onAction(() => nhNumberPause(n.id, pauseReason))}
              data-testid="admin-nh-pause-btn"
              disabled={!pauseReason}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-white px-4 py-2 font-semibold text-sm transition">
              <Pause className="w-4 h-4" /> Pause number
            </button>
          </>
        ) : (
          <button onClick={() => onAction(() => nhNumberRestore(n.id, "manual admin restore"))}
            data-testid="admin-nh-restore-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 font-semibold text-sm transition">
            <RotateCw className="w-4 h-4" /> Restore to healthy
          </button>
        )}
        {/* Simulate a spam-flag for testing */}
        <button onClick={() => onAction(() => nhEventRecord({ kind: "spam_flag", number_id: n.id }))}
          data-testid="admin-nh-simulate-spam"
          className="inline-flex items-center gap-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-4 py-2 font-semibold text-sm transition">
          <ShieldAlert className="w-4 h-4" /> Log spam flag
        </button>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Recent events (last 7d)</div>
        <div className="mt-3 space-y-1 max-h-[280px] overflow-y-auto">
          {(data.recent_events || []).map((e) => (
            <div key={e.id} className="rounded border border-slate-100 bg-slate-50 p-2 text-xs" data-testid={`admin-nh-event-${e.kind}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-800">{e.kind}</div>
                <div className="text-[10px] text-slate-500">{new Date(e.at).toLocaleString()}</div>
              </div>
              {e.payload?.reason && <div className="mt-0.5 text-slate-600">{e.payload.reason}</div>}
            </div>
          ))}
          {(data.recent_events || []).length === 0 && <div className="text-xs text-slate-500 p-3">No events yet.</div>}
        </div>
      </div>
    </div>
  );
}

function AddNumberModal({ onClose, onAdded }) {
  const [e164, setE164] = useState("+1");
  const [label, setLabel] = useState("");
  const [country, setCountry] = useState("US");
  const [region, setRegion] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      await nhNumberAdd({ e164, label, country, region });
      onAdded();
    } catch (e) {
      setError(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-950/60 z-50 flex items-center justify-center p-4" data-testid="admin-nh-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <div className="text-lg font-bold text-slate-900">Register a number</div>
        <div className="mt-4 space-y-3">
          <SetField label="E.164 number (starts with +)"><input value={e164} onChange={(e) => setE164(e.target.value)} data-testid="admin-nh-add-e164" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" /></SetField>
          <SetField label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="admin-nh-add-label" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="e.g. Sales line — Bay Area" /></SetField>
          <div className="grid grid-cols-2 gap-2">
            <SetField label="Country"><input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="admin-nh-add-country" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
            <SetField label="Region / state"><input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="admin-nh-add-region" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
          </div>
          {error && <div className="text-sm text-rose-700">{error}</div>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="admin-nh-add-submit"
            className="rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white px-4 py-2 text-sm font-semibold">Register</button>
        </div>
      </div>
    </div>
  );
}

function PolicyPanel({ policy, onSave }) {
  const [spam, setSpam] = useState(policy.spam_flag_threshold);
  const [errT, setErrT] = useState(policy.carrier_error_threshold);
  const [autoP, setAutoP] = useState(policy.auto_pause);
  return (
    <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5" data-testid="admin-nh-policy">
      <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Health policy</div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <SetField label="Spam-flag threshold (7d)"><input type="number" value={spam} onChange={(e) => setSpam(e.target.value)} data-testid="admin-nh-policy-spam" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
        <SetField label="Carrier-error threshold (24h)"><input type="number" value={errT} onChange={(e) => setErrT(e.target.value)} data-testid="admin-nh-policy-err" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></SetField>
        <SetField label="Auto-pause on threshold">
          <div className="flex items-center gap-2 py-2">
            <input type="checkbox" checked={autoP} onChange={(e) => setAutoP(e.target.checked)} data-testid="admin-nh-policy-auto" className="w-4 h-4 accent-indigo-600" />
            <span className="text-sm">{autoP ? "On" : "Degrade only (no auto-pause)"}</span>
          </div>
        </SetField>
        <button onClick={() => onSave({ spam_flag_threshold: Number(spam), carrier_error_threshold: Number(errT), auto_pause: autoP })}
          data-testid="admin-nh-policy-save"
          className="rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 text-sm font-semibold">Save policy</button>
      </div>
    </div>
  );
}

function SetField({ label, children }) {
  return (<label className="block"><div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>);
}
function Metric({ label, value, tone = "slate" }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-800",
                emerald: "bg-emerald-50 text-emerald-800",
                amber: "bg-amber-50 text-amber-800",
                rose: "bg-rose-50 text-rose-700",
                slate: "bg-slate-50 text-slate-700" }[tone];
  return (<div className={`rounded-lg border border-slate-200 p-3 ${bg}`}><div className="text-[10px] uppercase font-bold tracking-widest opacity-75">{label}</div><div className="mt-1 text-xl font-black">{value}</div></div>);
}
function Tile({ label, value, tone, testId }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
                emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
                amber: "bg-amber-50 text-amber-800 border-amber-200",
                rose: "bg-rose-50 text-rose-700 border-rose-200",
                slate: "bg-slate-100 text-slate-700 border-slate-200" }[tone];
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-75">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}
